// frontend/src/pages/admin/AdminPointsManage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { FaCoins, FaSearch, FaGift, FaStore } from 'react-icons/fa';

// ── 共通型 ──
interface UserBalance { user_id: number; balance: number; email: string | null; name: string | null; }
interface HistoryItem { id: number; amount: number; type: string; source: string; description: string | null; created_at: string; }
interface Reward { id: number; name: string; description: string | null; points_cost: number; reward_type: string; is_active: boolean; sort_order: number; }
interface Redemption { id: number; user_id: number; user_email: string | null; user_name: string | null; reward_name: string; reward_type: string; points_spent: number; status: string; admin_note: string | null; created_at: string; processed_at: string | null; }

const TYPE_COLOR: Record<string, string> = { earn: 'text-green-600', spend: 'text-red-500', expire: 'text-gray-400', adjust: 'text-blue-500' };
const STATUS_COLOR: Record<string, string> = { pending: 'bg-yellow-100 text-yellow-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-500' };
const STATUS_LABEL: Record<string, string> = { pending: '審査中', approved: '承認済', rejected: '却下' };

// ═══════════════════════════════════════════
// Tab1: ポイント残高・手動調整
// ═══════════════════════════════════════════
const TabBalance: React.FC = () => {
  const [users, setUsers] = useState<UserBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<UserBalance | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [histBal, setHistBal] = useState(0);
  const [adjAmount, setAdjAmount] = useState('');
  const [adjDesc, setAdjDesc] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/admin/points/users').then(r => setUsers(r.data)).catch(() => toast.error('取得失敗')).finally(() => setLoading(false));
  }, []);

  const openDetail = async (u: UserBalance) => {
    setSelected(u); setAdjAmount(''); setAdjDesc('');
    const r = await api.get(`/admin/points/users/${u.user_id}`);
    setHistBal(r.data.balance); setHistory(r.data.history);
  };

  const adjust = async () => {
    if (!selected || !adjAmount || !adjDesc) { toast.error('金額と理由を入力してください'); return; }
    const r = await api.post('/admin/points/adjust', { user_id: selected.user_id, amount: Number(adjAmount), description: adjDesc });
    toast.success(`調整完了。新残高: ${r.data.new_balance}pt`);
    openDetail(selected);
    api.get('/admin/points/users').then(r => setUsers(r.data));
  };

  const filtered = users.filter(u => !search || u.email?.includes(search) || u.name?.includes(search) || String(u.user_id).includes(search));

  if (loading) return <div className="p-4 text-gray-400">読み込み中...</div>;

  return (
    <div className="flex flex-col md:flex-row gap-4">
      <div className="flex-1 min-w-0">
        <div className="relative mb-3">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
          <input value={search} onChange={e => setSearch(e.target.value)} className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm" placeholder="メール・名前・IDで検索" />
        </div>
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr><th className="px-4 py-3 text-left">ユーザー</th><th className="px-4 py-3 text-right">残高</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(u => (
                <tr key={u.user_id} onClick={() => openDetail(u)} className={`cursor-pointer hover:bg-gray-50 ${selected?.user_id === u.user_id ? 'bg-coral/5' : ''}`}>
                  <td className="px-4 py-3"><div className="font-medium text-gray-800">{u.name || `User #${u.user_id}`}</div><div className="text-xs text-gray-400">{u.email}</div></td>
                  <td className="px-4 py-3 text-right font-bold text-yellow-600">{u.balance.toLocaleString()} pt</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={2} className="px-4 py-6 text-center text-gray-400">ポイント保有者なし</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {selected && (
        <div className="w-full md:w-80 md:shrink-0">
          <div className="bg-white rounded-xl shadow p-4">
            <div className="flex justify-between mb-3"><div><div className="font-semibold">{selected.name || `#${selected.user_id}`}</div><div className="text-xs text-gray-400">{selected.email}</div></div><div className="text-2xl font-bold text-yellow-500">{histBal.toLocaleString()}pt</div></div>
            <div className="border rounded-lg p-3 mb-3 space-y-2">
              <p className="text-xs font-semibold text-gray-600">手動調整・核銷</p>
              <input value={adjAmount} onChange={e => setAdjAmount(e.target.value)} type="number" className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm" placeholder="例: -500 / 200" />
              <input value={adjDesc} onChange={e => setAdjDesc(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm" placeholder="理由" />
              <button onClick={adjust} className="w-full bg-coral text-white rounded-lg py-1.5 text-sm font-medium">実行</button>
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {history.map(h => (
                <div key={h.id} className="flex justify-between text-xs py-1.5 border-b border-gray-50">
                  <div><span className={`font-semibold ${TYPE_COLOR[h.type] || ''}`}>{h.type}</span><div className="text-gray-400">{h.description || h.source} · {h.created_at?.slice(0,10)}</div></div>
                  <span className={`font-bold ${h.amount >= 0 ? 'text-green-600' : 'text-red-500'}`}>{h.amount >= 0 ? '+' : ''}{h.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// Tab2: 交換商品管理
// ═══════════════════════════════════════════
const TabRewards: React.FC = () => {
  const { t } = useTranslation();
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [editing, setEditing] = useState<Reward | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', points_cost: '', reward_type: 'amazon', sort_order: '0' });

  const load = () => api.get('/admin/points/rewards').then(r => setRewards(r.data)).catch(() => toast.error('取得失敗'));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: '', description: '', points_cost: '', reward_type: 'amazon', sort_order: '0' }); setCreating(true); };
  const openEdit = (r: Reward) => { setEditing(r); setForm({ name: r.name, description: r.description || '', points_cost: String(r.points_cost), reward_type: r.reward_type, sort_order: String(r.sort_order) }); setCreating(true); };

  const save = async () => {
    const body = { name: form.name, description: form.description || null, points_cost: Number(form.points_cost), reward_type: form.reward_type, sort_order: Number(form.sort_order) };
    try {
      if (editing) { await api.patch(`/admin/points/rewards/${editing.id}`, body); toast.success('更新しました'); }
      else { await api.post('/admin/points/rewards', body); toast.success('作成しました'); }
      setCreating(false); load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || '保存失敗'); }
  };

  const toggle = async (r: Reward) => {
    await api.patch(`/admin/points/rewards/${r.id}`, { is_active: !r.is_active });
    load();
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={openCreate} className="bg-coral text-white px-4 py-2 rounded-lg text-sm font-medium">+ 商品を追加</button>
      </div>
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み。md 未満で表示） */}
        <div className="md:hidden divide-y divide-gray-100">
          {rewards.map(r => (
            <div key={r.id} className="p-4">
              {/* 商品名・説明 + 状態バッジ */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium break-words">{r.name}</div>
                  {r.description && <div className="text-xs text-gray-400 break-words mt-0.5">{r.description}</div>}
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{r.is_active ? '公開中' : '非公開'}</span>
              </div>
              {/* 必要pt・種別 */}
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div className="min-w-0">
                  <span className="text-gray-400">必要pt：</span>
                  <span className="font-bold text-amber-700 break-all">{r.points_cost.toLocaleString()}</span>
                </div>
                <div className="min-w-0">
                  <span className="text-gray-400">種別：</span>
                  <span className="text-gray-500 break-all">{r.reward_type}</span>
                </div>
              </div>
              {/* 操作（編集・公開/非公開トグル） */}
              <div className="mt-3 flex gap-3 flex-wrap">
                <button onClick={() => openEdit(r)} className="text-xs text-blue-500 hover:underline">編集</button>
                <button onClick={() => toggle(r)} className="text-xs text-gray-500 hover:underline">{r.is_active ? '非公開' : '公開'}</button>
              </div>
            </div>
          ))}
        </div>
        {/* ▼ デスクトップ: テーブル表示（md 以上で表示） */}
        <table className="hidden md:table w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr><th className="px-4 py-3 text-left">商品名</th><th className="px-4 py-3 text-center">必要pt</th><th className="px-4 py-3 text-center">種別</th><th className="px-4 py-3 text-center">状態</th><th className="px-4 py-3 text-center">操作</th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {rewards.map(r => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-3"><div className="font-medium">{r.name}</div><div className="text-xs text-gray-400">{r.description}</div></td>
                <td className="px-4 py-3 text-center font-bold text-amber-700">{r.points_cost.toLocaleString()}</td>
                <td className="px-4 py-3 text-center text-xs text-gray-500">{r.reward_type}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{r.is_active ? '公開中' : '非公開'}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex gap-2 justify-center">
                    <button onClick={() => openEdit(r)} className="text-xs text-blue-500 hover:underline">編集</button>
                    <button onClick={() => toggle(r)} className="text-xs text-gray-500 hover:underline">{r.is_active ? '非公開' : '公開'}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold mb-4">{editing ? t('points.rewards.edit') : t('points.rewards.add')}</h2>
            <div className="space-y-3">
              {([
                [t('points.rewards.name'), 'name', 'text'],
                [t('points.rewards.desc'), 'description', 'text'],
                [t('points.rewards.cost'), 'points_cost', 'number'],
                [t('points.rewards.sort'), 'sort_order', 'number'],
              ] as [string, string, string][]).map(([label, key, type]) => (
                <div key={key}><label className="text-xs text-gray-500 mb-1 block">{label}</label>
                  <input type={type} value={(form as any)[key]} onChange={e => setForm(f => ({...f, [key]: e.target.value}))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" /></div>
              ))}
              <div><label className="text-xs text-gray-500 mb-1 block">{t('points.rewards.type')}</label>
                <select value={form.reward_type} onChange={e => setForm(f => ({...f, reward_type: e.target.value}))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                  <option value="amazon">{t('points.reward_type.amazon')}</option>
                  <option value="shipping_regular">{t('points.reward_type.shipping_regular')}</option>
                  <option value="shipping_cold">{t('points.reward_type.shipping_cold')}</option>
                  <option value="other">{t('points.reward_type.other')}</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setCreating(false)} className="flex-1 border border-gray-200 text-gray-600 rounded-lg py-2 text-sm">キャンセル</button>
              <button onClick={save} className="flex-1 bg-coral text-white rounded-lg py-2 text-sm font-medium">保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// Tab3: 交換申請管理（核銷）
// ═══════════════════════════════════════════
const TabRedemptions: React.FC = () => {
  const [items, setItems] = useState<Redemption[]>([]);
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const [noteInput, setNoteInput] = useState<Record<number, string>>({});

  const load = useCallback(() => {
    const params = filter === 'all' ? {} : { status: 'pending' };
    api.get('/admin/points/redemptions', { params }).then(r => setItems(r.data)).catch(() => toast.error('取得失敗'));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const process = async (id: number, status: 'approved' | 'rejected') => {
    const note = noteInput[id] || '';
    const label = status === 'approved' ? '承認' : '却下';
    if (!window.confirm(`この申請を${label}しますか？`)) return;
    try {
      await api.patch(`/admin/points/redemptions/${id}`, { status, admin_note: note || null });
      toast.success(`${label}しました`);
      load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || '処理失敗'); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {(['pending', 'all'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-1.5 rounded-full text-sm font-medium border ${filter === f ? 'bg-coral text-white border-coral' : 'border-gray-200 text-gray-600'}`}>
            {f === 'pending' ? '審査待ち' : '全件'}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
        {items.length === 0 && <div className="px-4 py-8 text-center text-gray-400 text-sm">申請がありません</div>}
        {items.map(item => (
          <div key={item.id} className="px-4 py-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-sm">{item.user_name || `#${item.user_id}`} <span className="text-gray-400 text-xs ml-1">{item.user_email}</span></div>
                <div className="text-sm text-gray-700 mt-0.5">{item.reward_name}</div>
                <div className="text-xs text-gray-400 mt-0.5">{item.created_at?.slice(0, 10)} · {item.points_spent.toLocaleString()}pt</div>
                {item.admin_note && <div className="text-xs text-gray-500 mt-1">備考: {item.admin_note}</div>}
              </div>
              <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[item.status] || ''}`}>{STATUS_LABEL[item.status] || item.status}</span>
            </div>
            {item.status === 'pending' && (
              <div className="mt-3 flex gap-2 items-center">
                <input value={noteInput[item.id] || ''} onChange={e => setNoteInput(n => ({...n, [item.id]: e.target.value}))} className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-xs" placeholder="備考（任意）" />
                <button onClick={() => process(item.id, 'approved')} className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium">承認</button>
                <button onClick={() => process(item.id, 'rejected')} className="bg-red-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium">却下</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// メインコンポーネント
// ═══════════════════════════════════════════
const AdminPointsManage: React.FC = () => {
  const [tab, setTab] = useState<'balance' | 'rewards' | 'redemptions'>('balance');
  const TABS = [
    { key: 'balance',     label: '残高管理',   icon: <FaCoins /> },
    { key: 'rewards',     label: '交換商品',   icon: <FaStore /> },
    { key: 'redemptions', label: '申請管理',   icon: <FaGift /> },
  ] as const;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <FaCoins className="text-yellow-500 text-2xl" />
        <h1 className="text-2xl font-bold text-gray-800">Health Point 管理</h1>
      </div>
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? 'bg-white text-coral shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>
      {tab === 'balance'     && <TabBalance />}
      {tab === 'rewards'     && <TabRewards />}
      {tab === 'redemptions' && <TabRedemptions />}
    </div>
  );
};

export default AdminPointsManage;
